import DomPurify from 'dompurify';
import { useDevice } from '@/app/hooks';
import { hysdk } from '@/app/utils/client';

export const useRenderFormat = () => {
  const { isLenovoMini } = useDevice();
  const renderHtmlContent = (content: string | undefined) => {
    if (!content) return null;
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    const safeHtml = DomPurify.sanitize(doc.body.innerHTML, {
      ADD_ATTR: isLenovoMini
        ? []
        : ['target', 'preload', 'playsinline', 'muted', 'webkit-playsinline', 'controls', 'poster'], // 添加 controls 和 poster
    });

    const handleVideoElement = (el: HTMLDivElement | null) => {
      if (el && hysdk.sniff.os === 'ios') {
        // biome-ignore lint/suspicious/useIterableCallbackReturn: each callback configures a video element in place
        el.querySelectorAll('video').forEach((video) => {
          // 1. 强制加载视频并显示控件，提高浏览器加载优先级
          // video.setAttribute('controls', '');
          // video.setAttribute('crossorigin', 'anonymous'); // 尝试解决CORS问题
          video.setAttribute('preload', 'metadata'); // 先加载元数据可能更可靠

          const generatePoster = () => {
            try {
              const canvas = document.createElement('canvas');
              // 确保尺寸大于0
              canvas.width = Math.max(video.videoWidth || 320, 1);
              canvas.height = Math.max(video.videoHeight || 240, 1);
              const ctx = canvas.getContext('2d');

              if (!ctx) {
                console.error('无法获取canvas上下文');
                return;
              }

              // 尝试绘制当前帧
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

              try {
                const posterUrl = canvas.toDataURL('image/jpeg', 0.8);

                if (posterUrl.length > 100) {
                  // 有效的 base64 图像应该长度超过这个值
                  video.setAttribute('poster', posterUrl);
                  video.style.opacity = '';
                  video.style.position = '';

                  // 使用原生控件而不是自定义点击事件
                  video.setAttribute('controls', '');
                } else {
                  console.warn('生成的海报图像异常', posterUrl.substring(0, 50));
                }
              } catch (error) {
                console.error('生成海报URL失败:', error);
              }
            } catch (error) {
              console.error('生成海报过程中出错:', error);
            }
          };

          // 多个事件点尝试生成海报，增加成功机会
          video.addEventListener(
            'loadeddata',
            () => {
              video.currentTime = 0.1; // 略微偏离0，有时更容易触发seeked
              setTimeout(generatePoster, 100); // 即使事件未触发也尝试生成
            },
            { once: true },
          );

          video.addEventListener(
            'loadedmetadata',
            () => {
              video.currentTime = 0.1;
            },
            { once: true },
          );

          video.addEventListener('seeked', () => {
            generatePoster();
          });

          return () => {
            video.removeEventListener('loadeddata', () => {
              video.currentTime = 0.1;
              setTimeout(generatePoster, 100);
            });
            video.removeEventListener('loadedmetadata', () => {
              video.currentTime = 0.1;
            });
            video.removeEventListener('seeked', generatePoster);
          };
        });
      }
    };

    return (
      <div
        className="[&>li]:mb-2 [&>ol]:mb-4 [&>ol]:list-decimal [&>ol]:pl-6 [&>ul]:my-4 [&>ul]:list-disc [&>ul]:pl-6"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: <html content>
        dangerouslySetInnerHTML={{ __html: safeHtml }}
        ref={(el) => handleVideoElement(el)}
      />
    );
  };

  return {
    renderHtmlContent,
  };
};
